import { TEST_IDS } from "src/apps/config/testIds";
import { useDocumentationArea } from "src/apps/hooks/useDocumentationArea";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { useRepresentedEntityFormData } from "src/apps/routes/onboarding/components/RepresentedEntityDataForm/hooks/useRepresentedEntityFormData";
import { useRepresentedEntityForm } from "src/apps/routes/onboarding/components/RepresentedEntityDataForm/hooks/useRepresentedEntityForm";
import { FormContainer } from "src/apps/routes/onboarding/components/FormContainer";
import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";

import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { BoldHeader1 } from "src/apps/components/typography/Texts";
import {
  BUTTON_TEXT,
  TITLE,
} from "src/apps/routes/onboarding/components/RepresentedEntityDataForm/RepresentedEntityDataForm.constants";
import { CompanySection } from "src/apps/routes/onboarding/components/RepresentedEntityDataForm/components/CompanySection";
import { LegalRepresentativeSection } from "src/apps/routes/onboarding/components/RepresentedEntityDataForm/components/LegalRepresentativeSection";
import { Button } from "src/apps/components/button/Button";
import { ImArrowRight2 } from "react-icons/im";
import { useEffect, useState } from "react";
import { Select } from "src/apps/components/select/Select";
import { colors } from "@permisso/permisso-components";

const ACTOR_TEMPLATE_SELECTOR_TITLE = "documentation_area.sign_up_form.input.actor_template";
const ACTOR_TEMPLATE_SELECTOR_PLACEHOLDER =
  "documentation_area.sign_up_form.input.actor_template.placeholder";

export const RepresentedEntityDataForm = () => {
  const { t } = useTranslations();
  const { documentationArea, actorBasicData } = useDocumentationArea();
  if (!documentationArea) return null;

  const formData = useRepresentedEntityFormData(documentationArea, actorBasicData?.prefilledData);
  const availableTemplates = actorBasicData?.availableTemplates ?? [];
  const [selectedActorTemplateId, setSelectedActorTemplateId] = useState<string>(
    availableTemplates.length === 1 ? availableTemplates[0].id : "",
  );

  useEffect(() => {
    if (availableTemplates.length === 1) {
      setSelectedActorTemplateId(availableTemplates[0].id);
      return;
    }

    if (
      selectedActorTemplateId &&
      !availableTemplates.some(
        (availableTemplate) => availableTemplate.id === selectedActorTemplateId,
      )
    ) {
      setSelectedActorTemplateId("");
    }
  }, [availableTemplates, selectedActorTemplateId]);

  const selectedTemplate = availableTemplates.find(
    (availableTemplate) => availableTemplate.id === selectedActorTemplateId,
  );

  const { values, errors, setters, buttonDisabled, isPending, onSubmit } = useRepresentedEntityForm(
    documentationArea,
    formData,
    selectedTemplate
      ? {
          id: selectedTemplate.id,
          key: selectedTemplate.key,
          hasBeenSelectedByTheUser: availableTemplates.length > 1,
        }
      : undefined,
  );

  const isTemplateSelectionRequired = availableTemplates.length > 1;
  const isSubmitDisabled =
    buttonDisabled || (isTemplateSelectionRequired && !selectedActorTemplateId);

  return (
    <FormContainer>
      <Icon size={ICON_SIZES.s30} color={colors.grey800} path={ICON_PATHS.ID_CARD} />
      <BoldHeader1>{t(TITLE)}</BoldHeader1>

      <CompanySection
        companyNif={values.companyNif}
        companyName={values.companyName}
        companyNifError={errors.companyNifError}
        isCompanyNifEditable={formData.isRepresentedEntityEditable}
        isCompanyNameEditable={formData.isCompanyNameEditable}
        onCompanyNifChange={setters.setCompanyNif}
        onCompanyNameChange={setters.setCompanyName}
      />

      <LegalRepresentativeSection
        idNumber={values.idNumber}
        idNumberError={errors.idNumberError}
        representativeRelationType={values.representativeRelationType}
        representativeRelationTypeError={errors.representativeRelationTypeError}
        isRepresentativeIdNumberEditable={formData.isRepresentativeIdNumberEditable}
        isRepresentativeRelationTypeEditable={formData.isRepresentativeRelationTypeEditable}
        onIdNumberChange={setters.setIdNumber}
        onRepresentativeRelationTypeChange={setters.setRepresentativeRelationType}
      />

      {availableTemplates.length > 1 ? (
        <Select
          title={t(ACTOR_TEMPLATE_SELECTOR_TITLE)}
          placeholder={t(ACTOR_TEMPLATE_SELECTOR_PLACEHOLDER)}
          value={selectedActorTemplateId}
          options={availableTemplates.map((availableTemplate) => ({
            key: t(availableTemplate.key),
            value: availableTemplate.id,
          }))}
          setSelectedValue={setSelectedActorTemplateId}
          color={selectedActorTemplateId ? colors.grey700 : colors.grey500}
        />
      ) : null}

      <Button
        rightIcon={<ImArrowRight2 />}
        onClick={onSubmit}
        isLoading={isPending}
        text={t(BUTTON_TEXT)}
        disabled={isSubmitDisabled}
        dataTestId={TEST_IDS.representedEntityData.continueButton}
      />
    </FormContainer>
  );
};
